/* ==========================================================================
   Young Grandpa - UGC carousel drag/momentum/seamless-loop
   --------------------------------------------------------------------------
   Ported from .design-refs/ugc-carousel-demo.html. The physics (drag,
   momentum decay, autoplay drift, arrow-nudge easing, wrap-around) are
   unchanged from that reference - only the setup around them differs:

   - The reference built BOTH halves of the seamless loop from one JS data
     array (`data.map(cardHTML) + data.map(cardHTML)`). Here the first half
     is real, editor-managed content rendered server-side by
     blocks/yg-ugc-item.liquid (content_for 'blocks' in
     sections/yg-ugc-carousel.liquid) - only real DOM nodes there carry
     block.shopify_attributes, so editor select/reorder/add/remove keep
     working. On init this file clones that rendered set once to build the
     second half, stripping the editor attributes and re-suffixing the
     quote id / read-more data-target on the clone so both halves keep
     unique ids.
   - Multiple carousel instances can exist on one page, so all state lives
     in an `attach(root)` closure keyed by the root element (mirrors
     assets/yg-gallery.js's pattern) instead of the reference's single set
     of module-level variables.
   - The reference never needed to tear itself down. This one is scoped per
     section instance and explicitly destroyed on shopify:section:unload -
     without that, every section edit in the theme editor would leave a
     previous instance's requestAnimationFrame loop running forever on
     detached nodes.
   - The reference autoplayed unconditionally, and so does this - at every
     width, matching assets/yg-photo-strip.js. Drag takes over while a
     pointer is down and its momentum decay plays out on release, after
     which DRAG_RELEASE_PAUSE_MS of quiet passes and the drift resumes.
     The hover pause that suspends the drift for a mouse parked over the
     row is gated on `(hover: hover)` rather than running everywhere: on a
     touchscreen the emulated mouseenter that follows a tap has no matching
     mouseleave until the finger lands somewhere else, so ungated it would
     stall the drift indefinitely after any tap.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '[data-ugc-carousel]';
  var HOVER = window.matchMedia('(hover: hover)');
  var SPEED = 0.55;
  var INTENT_THRESHOLD = 10;
  var DRAG_RELEASE_PAUSE_MS = 1400;
  var NUDGE_PAUSE_MS = 1600;
  var NUDGE_DURATION_MS = 480;
  var VELOCITY_DECAY = 0.94;
  var VELOCITY_FLOOR = 0.02;

  /** sectionId -> controller with a destroy() method */
  var instances = Object.create(null);

  /**
   * Strips editor-only attributes (however the theme names them) from a
   * cloned subtree so the clone never gets picked up as a second, ambiguous
   * copy of the same block by the theme editor.
   *
   * @param {Element} root
   */
  function stripEditorAttrs(root) {
    var els = root.querySelectorAll('[data-shopify-editor-block-id], [data-block-id]');
    for (var i = 0; i < els.length; i++) {
      var attrs = Array.prototype.slice.call(els[i].attributes);
      for (var j = 0; j < attrs.length; j++) {
        var name = attrs[j].name;
        if (name.indexOf('data-shopify') === 0 || name === 'data-block-id') {
          els[i].removeAttribute(name);
        }
      }
    }
  }

  /**
   * Builds the seamless-loop second half by cloning the real, server-rendered
   * first half. Only runs once per fresh render (guarded by a data attribute
   * on the track, since resize/recalc must not re-clone).
   *
   * Each original is stamped with its position first, so the clone inherits
   * it: assets/yg-ugc-modal.js opens by index, and a tap on a cloned card
   * has to open the same entry as a tap on its original. The clones are
   * also tagged (data-ugc-clone) and hidden from assistive tech - they are
   * duplicate content, and without tabindex="-1" on their photo triggers
   * every entry would be two tab stops.
   *
   * @param {Element} track
   */
  function ensureClonedHalf(track) {
    if (track.dataset.ugcCloned === 'true') return;

    var originals = Array.prototype.slice.call(track.children);
    for (var i = 0; i < originals.length; i++) {
      originals[i].dataset.ugcIndex = String(i);
    }

    for (var j = 0; j < originals.length; j++) {
      var clone = originals[j].cloneNode(true);
      stripEditorAttrs(clone);
      clone.dataset.ugcClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      var focusables = clone.querySelectorAll('[tabindex="0"]');
      for (var k = 0; k < focusables.length; k++) {
        focusables[k].setAttribute('tabindex', '-1');
      }
      track.appendChild(clone);
    }

    track.dataset.ugcCloned = 'true';
  }

  /**
   * @param {Element} root
   * @returns {{destroy: function(): void}|null}
   */
  function attach(root) {
    var viewport = root.querySelector('[data-ugc-viewport]');
    var track = root.querySelector('[data-ugc-track]');
    var prevBtn = root.querySelector('[data-ugc-prev]');
    var nextBtn = root.querySelector('[data-ugc-next]');
    if (!viewport || !track || !track.children.length) return null;

    ensureClonedHalf(track);

    var destroyed = false;
    var rafId = null;

    var cardStep = 0;
    var pos = 0;
    var half = 0;
    var dragging = false;
    var startX = 0;
    var startY = 0;
    var startPos = 0;
    var velocity = 0;
    var lastX = 0;
    var lastT = 0;
    var idleTimer = null;
    var paused = false;
    var nudgeRaf = null;
    /* 'pending' | 'horizontal' | 'vertical' - which way a touch is heading,
       decided once it clears INTENT_THRESHOLD px in either axis. Lets
       touch-action: pan-y (assets/yg-custom.css) do its job: a mostly-
       vertical touch is left alone for native page scroll, a mostly-
       horizontal one is claimed by the drag below. */
    var intent = 'pending';

    function setTransform() {
      track.style.transform = 'translateX(' + pos + 'px)';
    }

    function wrap() {
      if (pos <= -half) pos += half;
      if (pos > 0) pos -= half;
    }

    function measure() {
      var first = track.children[0];
      var second = track.children[1];
      if (first && second) cardStep = second.offsetLeft - first.offsetLeft;
    }

    function recalc() {
      measure();
      half = track.scrollWidth / 2;
      wrap();
      setTransform();
    }

    function frame() {
      if (destroyed) return;

      if (!dragging) {
        if (Math.abs(velocity) > VELOCITY_FLOOR) {
          pos += velocity;
          velocity *= VELOCITY_DECAY;
        } else if (!paused) {
          pos -= SPEED;
        }
        wrap();
        setTransform();
      }
      rafId = requestAnimationFrame(frame);
    }

    function pauseFor(ms) {
      paused = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        paused = false;
      }, ms);
    }

    function onPointerDown(event) {
      /* viewport.setPointerCapture() below retargets every subsequent event
         for this pointer - including, in most browsers' real (trusted-
         event-only) click synthesis, the click itself - to viewport rather
         than wherever the finger/cursor actually is. For a plain tap that
         never moves, that's exactly the eventual click, and it fires (or
         has its default action resolved) against the wrong element -
         viewport, not the link - so the "Read more" link's href never
         navigates even though it's a real <a> with a correct href. None of
         that shows up in scripted testing: dispatchEvent()/.click() always
         fire a normal click with normal default-action semantics regardless
         of capture state, only a genuine trusted click goes through the
         browser's own synthesis that capture can hijack - which is why this
         needs a real click to reproduce, not just automated ones. Fix:
         never start a drag from an interactive descendant at all, so
         capture is never engaged for it and the tap/click stays entirely
         native.
      */
      if (event.target.closest && event.target.closest('a, button')) return;

      dragging = true;
      intent = 'pending';
      velocity = 0;
      startX = event.clientX;
      startY = event.clientY;
      startPos = pos;
      lastX = event.clientX;
      lastT = performance.now();
      viewport.classList.add('dragging');
      viewport.setPointerCapture(event.pointerId);
      clearTimeout(idleTimer);
      paused = true;
      /* No preventDefault here: touch-action: pan-y (assets/yg-custom.css)
         needs to stay free to start a native vertical scroll on its own if
         that's where this touch is headed. Intent isn't decided until the
         first move clears INTENT_THRESHOLD, below. */
    }

    /**
     * Resolves 'pending' intent to 'horizontal' or 'vertical' once movement
     * clears INTENT_THRESHOLD in either axis, and hands the gesture off to
     * native vertical scroll if it's vertical - releasing capture and
     * resetting drag state exactly like a pointerup/pointercancel would,
     * just without waiting for the browser to send one.
     *
     * @param {number} dx
     * @param {number} dy
     * @param {PointerEvent} event
     * @returns {boolean} true once intent is 'horizontal' and the caller
     *   should keep going; false if the move has nothing left to do (still
     *   pending, or just handed off to vertical scroll).
     */
    function resolveIntent(dx, dy, event) {
      if (intent !== 'pending') return intent === 'horizontal';

      if (Math.abs(dx) < INTENT_THRESHOLD && Math.abs(dy) < INTENT_THRESHOLD) return false;

      intent = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (intent === 'horizontal') return true;

      dragging = false;
      viewport.classList.remove('dragging');
      viewport.releasePointerCapture(event.pointerId);
      clearTimeout(idleTimer);
      paused = false;
      return false;
    }

    function onPointerMove(event) {
      if (!dragging) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      if (!resolveIntent(dx, dy, event)) return;

      /* Only reached once intent is 'horizontal' - this is what keeps
         vertical scroll locked out for the rest of THIS drag, since
         touch-action: pan-y alone would otherwise still let it creep in. */
      event.preventDefault();
      pos = startPos + dx;
      wrap();
      setTransform();
      var now = performance.now();
      var dt = now - lastT || 16;
      velocity = ((event.clientX - lastX) / dt) * 16;
      lastX = event.clientX;
      lastT = now;
    }

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('dragging');
      pauseFor(DRAG_RELEASE_PAUSE_MS);
    }

    function onMouseEnter() {
      if (!HOVER.matches || dragging) return;
      paused = true;
    }

    function onMouseLeave() {
      if (!HOVER.matches || dragging) return;
      clearTimeout(idleTimer);
      paused = false;
    }

    function nudge(dir) {
      clearTimeout(idleTimer);
      paused = true;
      if (nudgeRaf) cancelAnimationFrame(nudgeRaf);

      var target = pos + dir * -cardStep;
      var startVal = pos;
      var t0 = performance.now();

      function step(now) {
        if (destroyed) return;
        var t = Math.min(1, (now - t0) / NUDGE_DURATION_MS);
        var eased = 1 - Math.pow(1 - t, 3);
        pos = startVal + (target - startVal) * eased;
        wrap();
        setTransform();
        if (t < 1) {
          nudgeRaf = requestAnimationFrame(step);
        } else {
          nudgeRaf = null;
          pauseFor(NUDGE_PAUSE_MS);
        }
      }
      nudgeRaf = requestAnimationFrame(step);
    }

    function onPrevClick() {
      nudge(-1);
    }

    function onNextClick() {
      nudge(1);
    }

function onResize() {
      recalc();
    }

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('mouseenter', onMouseEnter);
    viewport.addEventListener('mouseleave', onMouseLeave);
    if (prevBtn) prevBtn.addEventListener('click', onPrevClick);
    if (nextBtn) nextBtn.addEventListener('click', onNextClick);
    window.addEventListener('resize', onResize);

    recalc();
    var recalcTimer = setTimeout(recalc, 200);
    rafId = requestAnimationFrame(frame);

    return {
      destroy: function () {
        destroyed = true;
        if (rafId) cancelAnimationFrame(rafId);
        if (nudgeRaf) cancelAnimationFrame(nudgeRaf);
        clearTimeout(idleTimer);
        clearTimeout(recalcTimer);
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', endDrag);
        viewport.removeEventListener('pointerleave', endDrag);
        viewport.removeEventListener('pointercancel', endDrag);
        viewport.removeEventListener('mouseenter', onMouseEnter);
        viewport.removeEventListener('mouseleave', onMouseLeave);
        if (prevBtn) prevBtn.removeEventListener('click', onPrevClick);
        if (nextBtn) nextBtn.removeEventListener('click', onNextClick);
        window.removeEventListener('resize', onResize);
      },
    };
  }

  function init() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var sectionId = root.dataset.sectionId;
      if (!sectionId || instances[sectionId]) continue;

      var controller = attach(root);
      if (controller) instances[sectionId] = controller;
    }
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    if (!sectionId || !instances[sectionId]) return;
    instances[sectionId].destroy();
    delete instances[sectionId];
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
